import { CSSProperties, useEffect, useState } from 'react';
import { Button, Divider, Drawer, Modal, message } from 'antd';
import { CheckCircleFilled, LoadingOutlined, MinusCircleFilled } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import {
  getSpsMetricTopItems,
  getSpsShopDiagnosis,
  getSpsShopMetrics,
  getSpsShopOverview,
} from './service';
import {
  BENEFIT_CHIP_LIMIT,
  DIMENSION_META,
  FETCH_ERROR_PHRASE,
  METRIC_META,
  SPS_SCORE_MAX,
} from './constants';
import { formatPercent, toNumber } from './utils';
import SpsStatusPill from './SpsStatusPill';
import SpsMetricCard from './SpsMetricCard';
import SpsMetricDetail from './SpsMetricDetail';

export interface SpsTrendDrawerProps {
  open: boolean;
  storeId: number;
  /** 店铺名/别名，由调用方按 isStoreAliasFirst 算好后传入；这里只做拼接 */
  storeLabel?: string;
  /** 打开时默认选中的维度 Tab —— 传榜单当前所在的维度 */
  initDimension?: Dashboard.TikTokUS.DimensionName;
  onClose: () => void;
}

/** 顶部环形：设计稿里环很粗、直径小，环内百分比数字大 + 小号 % */
function Ring({ percent, color }: { percent: number; color: string }) {
  const size = 62;
  const stroke = 13;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = circumference * Math.max(0, Math.min(1, percent));

  return (
    <svg width={size} height={size} style={{ flex: '0 0 auto' }}>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--fett-border-default)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth={stroke}
        strokeDasharray={`${dash} ${circumference}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="middle"
        textAnchor="middle"
        fill="var(--fett-text-primary)"
      >
        <tspan fontSize={18} fontWeight={600}>
          {Math.round(percent * 100)}
        </tspan>
        <tspan fontSize={11}>%</tspan>
      </text>
    </svg>
  );
}

/** 权益 chip 前置状态图标：已解锁=绿对勾，未解锁=灰减号 */
const BenefitIcon = ({ unlocked }: { unlocked: boolean }) =>
  unlocked ? (
    <CheckCircleFilled style={{ color: 'var(--fett-surface-success-text)', fontSize: 12 }} />
  ) : (
    <MinusCircleFilled style={{ color: 'var(--fett-text-quaternary)', fontSize: 12 }} />
  );

function SpsTrendDrawer({
  open,
  storeId,
  storeLabel,
  initDimension,
  onClose,
}: SpsTrendDrawerProps) {
  const { t } = useTranslate();
  const onError = () => message.error(t(FETCH_ERROR_PHRASE));

  /* ===== 打开抽屉只发这两个（其余等用户点指标卡） ===== */
  const { data: overview, loading: overviewLoading } = useRequest(
    () => getSpsShopOverview({ storeId }),
    { ready: open, refreshDeps: [open, storeId], onError },
  );

  const { data: metrics } = useRequest(() => getSpsShopMetrics({ storeId }), {
    ready: open,
    refreshDeps: [open, storeId],
    onError,
  });

  const [dimension, setDimension] = useState<Dashboard.TikTokUS.DimensionName>(
    initDimension ?? 'Product satisfaction',
  );

  /**
   * 每次打开都把 Tab 同步成**榜单当前所在的维度**（同一店铺重复打开时组件不会重新挂载，
   * 只靠 useState 初值会沿用上次在抽屉里切过的 Tab）。
   */
  useEffect(() => {
    if (open) {
      setDimension(initDimension ?? 'Product satisfaction');
    }
    // 只在「打开」这一刻同步；打开期间的切换以抽屉自身状态为准
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [selectedMetric, setSelectedMetric] = useState<Dashboard.TikTokUS.MetricCode>('NRR');

  /** 切维度默认选中新维度的第一张卡（切 Tab 本身不重新请求接口，6 个指标是一次拿全的） */
  useEffect(() => {
    const first = DIMENSION_META.find(({ value }) => value === dimension)?.metrics[0];
    if (first) {
      setSelectedMetric(first);
    }
  }, [dimension]);

  const currentMetrics = DIMENSION_META.find(({ value }) => value === dimension)?.metrics ?? [];

  const selectedItem = metrics?.find(({ metricCode }) => metricCode === selectedMetric);
  // status = NIL 表示未满足出分条件，**不是 0 分**
  const isNil = !selectedItem || selectedItem.status === 'NIL';
  const supportsTopItems = METRIC_META[selectedMetric].supportsTopItems;

  /** 点指标卡才拉；top-items 最贵且常被连点 → 加防抖 */
  const { data: diagnosis } = useRequest(
    () =>
      isNil
        ? Promise.resolve(undefined)
        : getSpsShopDiagnosis({ storeId, metricCode: selectedMetric }),
    { refreshDeps: [storeId, selectedMetric, isNil], debounceWait: 300, onError },
  );

  const { data: topItems } = useRequest(
    () =>
      isNil || !supportsTopItems
        ? Promise.resolve(undefined)
        : getSpsMetricTopItems({
            storeId,
            metricCode: selectedMetric as Dashboard.TikTokUS.TopItemsMetricCode,
          }),
    { refreshDeps: [storeId, selectedMetric, isNil, supportsTopItems], debounceWait: 300, onError },
  );

  /* ===== 权益 ===== */
  const [benefitModalOpen, setBenefitModalOpen] = useState(false);
  const benefits = overview?.benefits ?? [];
  const unlockedCount = benefits.filter(({ is_unlocked: unlocked }) => unlocked).length;
  // 顶部只放前 N 个：已解锁优先，其余进「查看全部」弹窗
  const benefitChips = [...benefits]
    .sort((a, b) => Number(b.is_unlocked) - Number(a.is_unlocked))
    .slice(0, BENEFIT_CHIP_LIMIT);

  /* ===== 顶部总览 ===== */
  const score = toNumber(overview?.spsScore);
  const percent = score === null ? 0 : score / SPS_SCORE_MAX;
  const ringColor =
    overview?.spsTier === 'CRITICAL' || overview?.spsTier === 'POOR'
      ? 'var(--ant-error-color)'
      : 'var(--ant-success-color)';

  /** 数据更新于只展示到日（接口给的是 yyyy-MM-dd HH:mm:ss） */
  const updateDate = overview?.dataUpdateTime ? overview.dataUpdateTime.slice(0, 10) : '-';

  return (
    <Drawer
      width="85vw"
      title={`${t('查看趋势')}${storeLabel ? ` · ${storeLabel}` : ''}`}
      visible={open}
      onClose={onClose}
      destroyOnClose
      contentWrapperStyle={{ maxWidth: '1200px' }}
    >
      <Container>
        {overviewLoading && !overview ? (
          <Section>
            <LoadingOutlined />
          </Section>
        ) : (
          <>
            <div
              style={{
                padding: 16,
                marginBottom: 16,
                borderRadius: 8,
                background: 'var(--fett-bg-panel)',
              }}
            >
              {/* ① 总览：左=环形+评分（分档胶囊在评分下方）；右=数据更新于 + 同类百分位/主类目 */}
              <div className="flex items-center" style={{ gap: 20 }}>
                <Ring percent={percent} color={ringColor} />
                <div className="flex flex-col" style={{ flex: '0 0 auto' }}>
                  <span style={labelStyle}>{t('SPS总分')}</span>
                  <div className="flex items-baseline" style={{ gap: 4 }}>
                    <span style={{ fontSize: 30, fontWeight: 600, lineHeight: 1.1 }}>
                      {overview?.spsScore ?? '-'}
                    </span>
                    <span style={{ fontSize: 14, color: 'var(--fett-text-tertiary)' }}>
                      /{SPS_SCORE_MAX}.0
                    </span>
                  </div>
                  {overview?.spsTier ? (
                    <span style={{ marginTop: 4 }}>
                      <SpsStatusPill status={overview.spsTier}>{overview.spsTier}</SpsStatusPill>
                    </span>
                  ) : null}
                </div>

                <div className="flex flex-col" style={{ flex: '1 0 0', minWidth: 0, gap: 6 }}>
                  <div style={{ textAlign: 'right', ...labelTextStyle }}>
                    {`${t('数据更新于：')}${updateDate}`}
                  </div>
                  <div className="flex" style={{ gap: 16 }}>
                    <SubBox>
                      <span style={labelStyle}>{t('同类百分位')}</span>
                      <span style={subBoxValueStyle}>
                        {formatPercent(overview?.peerPercentile)}
                      </span>
                    </SubBox>
                    <SubBox>
                      <span style={labelStyle}>{t('主类目')}</span>
                      <span style={subBoxValueStyle}>{overview?.categoryName || '-'}</span>
                    </SubBox>
                  </div>
                </div>
              </div>
              <Divider dashed type="horizontal" style={{ margin: '12px 0' }} />

              {/* ② 权益（上方用满宽分隔线与总览隔开） */}
              <div
                className="flex items-center"
                style={{ gap: 16, borderTop: '1px dashed var(--fett-border-default);' }}
              >
                <div className="flex flex-col" style={{ flex: '0 0 auto', marginRight: 16 }}>
                  <span style={labelStyle}>{t('权益已解锁')}</span>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>
                    {unlockedCount}/{benefits.length}
                  </span>
                </div>
                <div
                  className="flex items-center"
                  style={{ gap: 8, flex: '1 0 0', flexWrap: 'wrap' }}
                >
                  {benefitChips.map(
                    ({ benefit_name: name, is_unlocked: unlocked, unlock_score: need }) => {
                      const diff =
                        !unlocked && score !== null && toNumber(need) !== null
                          ? Number(need) - score
                          : null;
                      return (
                        <Chip key={name} unlocked={unlocked}>
                          <BenefitIcon unlocked={unlocked} />
                          {/* ⚠️ 官方值可能带尾随空格，必须 trim */}
                          <span>{name.trim()}</span>
                          {/* 未解锁的 chip 额外标注差多少分（设计稿如此） */}
                          {!unlocked && (
                            <span>{` ${t('待解锁')}${
                              diff !== null && diff > 0
                                ? t('（差x分）', { x: diff.toFixed(1) })
                                : ''
                            }`}</span>
                          )}
                        </Chip>
                      );
                    },
                  )}
                </div>
                {/* 设计稿批注：「权益最多展示 4 个，点击查看全部出弹窗」 */}
                {benefits.length > benefitChips.length && (
                  <Button type="link" onClick={() => setBenefitModalOpen(true)}>
                    {t('filter.all')}
                  </Button>
                )}
              </div>
            </div>

            {/* ③ 维度 Tab：下划线式 */}
            <TabsRow>
              {DIMENSION_META.map(({ value, label }) => (
                <span
                  key={value}
                  className={value === dimension ? 'active' : ''}
                  onClick={() => setDimension(value)}
                >
                  {t(label)}
                </span>
              ))}
            </TabsRow>

            {/* ④ 两张指标卡：两块独立白区，选中态左边框加粗 */}
            <div className="flex" style={{ gap: 16, padding: '16px 0' }}>
              {currentMetrics.map((code) => (
                <SpsMetricCard
                  key={code}
                  code={code}
                  item={metrics?.find(({ metricCode }) => metricCode === code)}
                  selected={code === selectedMetric}
                  onClick={() => setSelectedMetric(code)}
                />
              ))}
            </div>

            {/* ⑤ 口径/阈值、趋势、Top 商品、AI 分析 */}
            <SpsMetricDetail
              metricCode={selectedMetric}
              diagnosis={diagnosis}
              topItems={topItems}
              nil={isNil}
            />
          </>
        )}
      </Container>

      {/* 查看全部权益弹窗 */}
      <Modal
        visible={benefitModalOpen}
        footer={null}
        width={560}
        onCancel={() => setBenefitModalOpen(false)}
        title={
          <div className="flex items-center" style={{ gap: 8 }}>
            <span>{t('查看全部权益')}</span>
            <span
              style={{
                padding: '0 8px',
                borderRadius: 4,
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--ant-primary-color)',
                background: 'var(--fett-bg-info-tip)',
              }}
            >
              {`${t('已解锁')} ${unlockedCount} / ${benefits.length}`}
            </span>
          </div>
        }
      >
        {/* 当前 SPS 评分：浅蓝底条 */}
        <div
          className="flex items-center"
          style={{
            gap: 12,
            padding: '10px 16px',
            marginBottom: 8,
            borderRadius: 6,
            background: 'var(--fett-bg-info-tip)',
          }}
        >
          <span style={{ color: 'var(--fett-text-secondary)' }}>{t('当前 SPS 评分')}</span>
          <span style={{ fontSize: 20, fontWeight: 600, color: 'var(--ant-primary-color)' }}>
            {overview?.spsScore ?? '-'}
          </span>
          <span style={{ color: 'var(--fett-text-tertiary)' }}>/ {SPS_SCORE_MAX}.0</span>
        </div>

        {/* 权益列表 */}
        {benefits.map(({ benefit_name: name, is_unlocked: unlocked, unlock_score: need }) => {
          const diff =
            !unlocked && score !== null && toNumber(need) !== null ? Number(need) - score : null;
          return (
            <BenefitRow key={name}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--fett-text-primary)' }}>
                  {name.trim()}
                </div>
                <div style={{ fontSize: 13, color: 'var(--fett-text-tertiary)' }}>
                  {`${t('解锁条件：')}SPS ≥ ${need}`}
                  {diff !== null && diff > 0 && (
                    <span style={{ color: 'var(--ant-warning-color)' }}>
                      {`　${t('当前x，还差y分', {
                        x: overview?.spsScore,
                        y: diff.toFixed(1),
                      })}`}
                    </span>
                  )}
                </div>
              </div>
              <SpsStatusPill status={unlocked ? 'GOOD' : 'NIL'}>
                {unlocked ? t('已解锁') : t('待解锁')}
              </SpsStatusPill>
            </BenefitRow>
          );
        })}
      </Modal>
    </Drawer>
  );
}

const Container = styled.div`
  margin: -24px;
  padding: 16px;
  background: #f8f9fb;
`;

const labelStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--fett-text-tertiary)',
  marginBottom: 2,
};

const labelTextStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--fett-text-tertiary)',
};

const subBoxValueStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
};

/** 抽屉内每一块。`divider` 控制上方是否画满宽分隔线（与设计稿的分区一致） */
const Section = styled.section<{ divider?: boolean }>`
  padding: 16px 20px;
  ${({ divider }) => (divider ? 'border-top: 1px dashed var(--fett-border-default);' : '')}
`;

/** 「同类百分位 / 主类目」：设计稿是浅灰底圆角块，没有描边 */
const SubBox = styled.div`
  flex: 1 0 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  padding: 10px 16px;
  border-radius: 8px;
  background: var(--fett-bg-subtle);
`;

/** 权益 chip：浅绿底+绿描边+绿字（已解锁）／浅灰底+灰字（未解锁） */
const Chip = styled.span<{ unlocked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  font-size: 12px;
  border-radius: 14px;
  border: 1px solid
    ${({ unlocked }) =>
      unlocked ? 'var(--fett-surface-success-text)' : 'var(--fett-border-default)'};
  background: ${({ unlocked }) =>
    unlocked ? 'var(--fett-surface-success-bg)' : 'var(--fett-bg-subtle)'};
  color: ${({ unlocked }) =>
    unlocked ? 'var(--fett-surface-success-text)' : 'var(--fett-text-tertiary)'};
`;

const TabsRow = styled.div`
  display: flex;
  gap: 32px;
  padding: 0 20px;
  border-radius: 8px;
  background: var(--fett-bg-panel);
  border-bottom: 1px solid var(--fett-border-default);
  > span {
    position: relative;
    padding: 14px 0;
    font-size: 14px;
    cursor: pointer;
    color: var(--fett-text-tertiary);
    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1px;
      height: 2px;
      background: transparent;
    }
    &.active {
      color: var(--ant-primary-color);
      font-weight: 600;
      &::after {
        background: var(--ant-primary-color);
      }
    }
  }
`;

const BenefitRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 0;
  & + & {
    border-top: 1px solid var(--fett-border-default);
  }
`;

export default SpsTrendDrawer;
